import type { User } from '@/types'

/**
 * Check if an email already exists in the system
 * @param email - The email address to check
 * @returns Promise<boolean> - True if email exists, false otherwise
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
    
    const response = await fetch(`${apiUrl}/auth/check-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    })

    if (!response.ok) {
      // If endpoint doesn't exist or fails, assume email doesn't exist
      // The signup process will catch duplicates anyway
      return false
    }

    const data = await response.json()
    return data.exists === true
  } catch (error) {
    console.error('Email existence check failed:', error)
    // Return false so signup can proceed - the auth system will handle duplicates
    return false
  }
}

/**
 * Get user information by email (admin function)
 * @param email - The email address to look up
 * @returns Promise<User | null> - User data if found, null otherwise
 */
export async function getUserByEmail(email: string): Promise<User | null> {
  try {
    const token = localStorage.getItem('authToken')
    if (!token) {
      return null
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
    
    const response = await fetch(`${apiUrl}/users/by-email?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data.user || null
  } catch (error) {
    console.error('Error in getUserByEmail:', error)
    return null
  }
}

/**
 * Validate email format using regex
 * @param email - The email address to validate
 * @returns boolean - True if email format is valid
 */
export function isValidEmailFormat(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Comprehensive email validation including format and existence
 * @param email - The email address to validate
 * @returns Promise<{isValid: boolean, error?: string}> - Validation result
 */
export async function validateEmail(email: string): Promise<{isValid: boolean, error?: string}> {
  // First check email format
  if (!isValidEmailFormat(email)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    }
  }

  // Check if email already exists
  try {
    const exists = await checkEmailExists(email)
    if (exists) {
      return {
        isValid: false,
        error: 'An account with this email already exists. Please sign in instead.'
      }
    }
  } catch (error) {
    console.error('Email validation error:', error)
    // If we can't check email existence, allow the signup to proceed
    // The auth system will handle duplicate emails during signup
    console.warn('Could not verify email existence, allowing signup to proceed')
    return { isValid: true }
  }

  return { isValid: true }
}

/**
 * Check if an email is available for signup (not in use by active accounts)
 * This function considers deleted accounts as available for reuse
 * @param email - The email address to check
 * @returns Promise<{isAvailable: boolean, error?: string}> - Availability result
 */
export async function checkEmailAvailability(email: string): Promise<{isAvailable: boolean, error?: string}> {
  // First check email format
  if (!isValidEmailFormat(email)) {
    return {
      isAvailable: false,
      error: 'Please enter a valid email address'
    }
  }

  try {
    // Check if email exists in database
    const exists = await checkEmailExists(email)
    
    if (exists) {
      return {
        isAvailable: false,
        error: 'An account with this email already exists. Please sign in instead.'
      }
    }

    return { isAvailable: true }
  } catch (error) {
    console.error('Email availability check error:', error)
    return {
      isAvailable: false,
      error: 'Unable to verify email availability. Please try again.'
    }
  }
} 
